/**
 * CartPage — /cart
 *
 * Full-page shopping cart. Sub-total, per-item quantity controls, remove,
 * clear, and "Checkout" that redirects to Stripe hosted checkout.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/lib/useCart";
import {
  ShoppingBag, Trash2, Minus, Plus, Loader2, ArrowLeft, CreditCard, Building2,
} from "lucide-react";

function fmt(amount, currency = "usd") {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(amount); }
  catch { return `$${amount.toFixed(2)}`; }
}

export default function CartPage() {
  const { user } = useAuth();
  const { cart, subtotal, currency, count, loading, updateQty, remove, clear } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const nav = useNavigate();

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="cart-signin">
        <ShoppingBag className="w-10 h-10 mx-auto opacity-40 mb-2" />
        <h1 className="font-display text-2xl mb-2">Sign in to see your cart</h1>
        <Link to="/login?redirect=/cart" className="wofy-btn-primary inline-flex mt-2">Sign in</Link>
      </div>
    );
  }

  if (loading) return <div className="max-w-3xl mx-auto py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>;

  const empty = count === 0;

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await http.post("/checkout/create-session", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setCheckingOut(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="cart-page">
      <Link to="/businesses" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Continue shopping
      </Link>
      <h1 className="font-display text-4xl mb-6">Your cart {count > 0 && <span className="text-xl opacity-60">({count} {count === 1 ? "item" : "items"})</span>}</h1>

      {empty ? (
        <div className="wofy-card !p-10 text-center max-w-lg mx-auto" data-testid="cart-empty">
          <ShoppingBag className="w-10 h-10 mx-auto opacity-40 mb-2" />
          <p className="font-display text-xl mb-1">Your cart is empty</p>
          <p className="text-sm opacity-70 mb-4">Explore local businesses and discover unique products.</p>
          <Link to="/businesses" className="wofy-btn-primary inline-flex items-center gap-1.5">
            <Building2 className="w-4 h-4" /> Explore businesses
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-[1fr_320px] gap-6">
          <ul className="space-y-3" data-testid="cart-items">
            {cart.items.map((it) => (
              <li key={it.id} className="wofy-card !p-4 flex items-center gap-4" data-testid={`cart-item-${it.id}`}>
                <div className="w-16 h-16 rounded-lg bg-black/5 shrink-0 overflow-hidden">
                  {it.image_url
                    ? <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full grid place-items-center opacity-30 text-2xl">📦</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <Link to={`/business/${it.business_slug}`} className="text-[11px] opacity-70 hover:underline inline-flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {it.business_name}
                  </Link>
                  <div className="text-xs opacity-80 mt-1">{fmt(it.price, it.currency)}</div>
                </div>
                <div className="inline-flex items-center gap-1">
                  <button onClick={() => updateQty(it.id, Math.max(1, it.quantity - 1))} className="w-7 h-7 rounded-full border grid place-items-center hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid={`cart-decr-${it.id}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-8 text-center text-sm" data-testid={`cart-qty-${it.id}`}>{it.quantity}</span>
                  <button onClick={() => updateQty(it.id, it.quantity + 1)} className="w-7 h-7 rounded-full border grid place-items-center hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid={`cart-incr-${it.id}`}><Plus className="w-3 h-3" /></button>
                </div>
                <div className="text-sm font-medium tabular-nums w-20 text-right">{fmt(it.price * it.quantity, it.currency)}</div>
                <button onClick={() => remove(it.id)} className="text-neutral-500 hover:text-red-600 p-1" title="Remove" data-testid={`cart-remove-${it.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
            <li className="text-right">
              <button onClick={clear} className="text-xs underline opacity-70 hover:opacity-100" data-testid="cart-clear">Clear cart</button>
            </li>
          </ul>

          <aside className="wofy-card !p-5 h-fit sticky top-4" data-testid="cart-summary">
            <div className="overline mb-2">Order summary</div>
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between"><dt>Subtotal</dt><dd className="tabular-nums">{fmt(subtotal, currency)}</dd></div>
              <div className="flex justify-between opacity-70"><dt>Shipping</dt><dd className="text-[11px] italic">calculated at checkout</dd></div>
              <div className="flex justify-between opacity-70"><dt>Tax</dt><dd className="text-[11px] italic">calculated at checkout</dd></div>
              <div className="border-t pt-2 mt-2 flex justify-between font-display text-lg" style={{ borderColor: "var(--wofy-border)" }}>
                <dt>Total</dt><dd className="tabular-nums">{fmt(subtotal, currency)}</dd>
              </div>
            </dl>
            <button
              onClick={startCheckout}
              disabled={checkingOut}
              className="wofy-btn-primary w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="cart-checkout"
            >
              {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {checkingOut ? "Redirecting…" : "Checkout with Stripe"}
            </button>
            <p className="text-[10px] opacity-60 mt-3 text-center">
              Secure payment powered by Stripe. Test mode — use card <b>4242 4242 4242 4242</b>, any future expiry, any CVC.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
